import { Checkbox, Input } from '@prio365/prio365-react-library';
import { Form } from 'antd';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'react-jss';
import Flex from '../../../../../../components/Flex';
import { IncomingInvoiceDto } from '../../../../../../models/IncomingInvoices';
import { PrioTheme } from '../../../../../../theme/types';
import { makePrioStyles } from '../../../../../../theme/utils';
import OfficePicker from '../../../../../companies/components/OfficePickerPrioCL';
import { useIncomingInvoiceDrawerContext } from '../incomingInvoiceComponents/IncomingInvoiceContextProvider';
import IncomingInvoiceDrawerSectionHeader from '../incomingInvoiceComponents/IncomingInvoiceDrawerSectionHeader';

const useStyles = makePrioStyles((theme) => ({
  root: {},
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    rowGap: theme.spacing.regular,
    columnGap: theme.spacing.small,
  },
  formItem: {
    marginBottom: 0,
    transition: 'none',
  },
  fieldValue: {
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.default,
  },
  fromOfficeFieldWrapper: {
    width: '100%',
  },
}));

export enum ControllingStepSectionCategory {
  ProjectAssignment = 'ProjectAssignment',
  CompanyAssignment = 'CompanyAssignment',
  ApprovalWorkflow = 'ApprovalWorkflow',
}

interface IncomingInvoicesDrawerControllingStepProps {
  className?: string;
}

export const IncomingInvoicesDrawerControllingStep: React.FC<
  IncomingInvoicesDrawerControllingStepProps
> = ({ className }) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const { form, setHasUnsavedChanges } = useIncomingInvoiceDrawerContext();

  const {
    fromOfficeId = null,
    toOfficeId = null,
    fromOfficeName = '',
  } = form.getFieldsValue([
    'fromOfficeId',
    'fromOfficeName',
    'toOfficeId',
  ]) as Pick<
    IncomingInvoiceDto,
    'fromOfficeId' | 'toOfficeId' | 'fromOfficeName'
  >;
  const [customNameToggled, setCustomNameToggled] = React.useState(
    fromOfficeName !== ''
  );
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const requiredValueRule = useMemo(
    () => (message: string) => ({
      required: true,
      validateTrigger: 'onBlur' as const,
      message,
      validator: (_: any, value: string | null) =>
        value ? Promise.resolve() : Promise.reject(),
    }),
    []
  );
  //#endregion

  return (
    <Flex.Column className={classNames(className)}>
      {/* Unternehmenszuordnung */}
      <div>
        <IncomingInvoiceDrawerSectionHeader
          title={t(
            'accounting:incomingInvoices.drawerSteps.controlling.sectionTitles.companyAssignment'
          )}
          iconProp={['fal', 'building']}
          disableEdit
        />
        <div className={classes.formGrid}>
          <Flex.Column
            childrenGap={theme.spacing.extraSmall}
            className={classes.fromOfficeFieldWrapper}
          >
            {!customNameToggled && (
              <Form.Item
                name="fromOfficeId"
                label={t(
                  'accounting:incomingInvoices.fields.fromOfficeId.label'
                )}
                rules={[
                  {
                    required: true,
                    validator: async (_: any, value: string | null) => {
                      return value ? Promise.resolve() : Promise.reject();
                    },
                    validateTrigger: 'onBlur' as const,
                    message: t(
                      'accounting:incomingInvoices.fields.fromOfficeId.requiredText'
                    ),
                  },
                ]}
                className={classes.formItem}
              >
                <OfficePicker
                  multiple={false}
                  allOffices
                  value={fromOfficeId}
                  onChange={(val) => {
                    const officeId = Array.isArray(val)
                      ? (val[0] ?? null)
                      : val;
                    form.setFieldsValue({ fromOfficeId: officeId });
                  }}
                />
              </Form.Item>
            )}

            {customNameToggled && (
              <Form.Item
                name="fromOfficeName"
                label={t(
                  'accounting:incomingInvoices.fields.fromOfficeName.label'
                )}
                rules={[
                  {
                    required: true,
                    validator: async (_: any, value: string) => {
                      return value && value.trim().length > 0
                        ? Promise.resolve()
                        : Promise.reject();
                    },
                    validateTrigger: 'onBlur' as const,
                    message: t(
                      'accounting:incomingInvoices.fields.fromOfficeName.requiredText'
                    ),
                  },
                ]}
                className={classes.formItem}
              >
                <Input
                  value={fromOfficeName}
                  full
                  placeholder={t(
                    'accounting:incomingInvoices.fields.fromOfficeName.placeholder'
                  )}
                  size="small"
                  onChange={(e) => {
                    form.setFieldsValue({ fromOfficeName: e });
                  }}
                />
              </Form.Item>
            )}

            <Flex.Row
              alignItems="center"
              childrenGap={theme.spacing.extraSmall}
            >
              <Checkbox
                checked={customNameToggled}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setHasUnsavedChanges(true);
                  setCustomNameToggled(checked);
                  if (checked) {
                    form.setFieldsValue({ fromOfficeId: null });
                  } else {
                    form.setFieldsValue({ fromOfficeName: '' });
                  }
                }}
                size="small"
              />
              <span
                style={{
                  color: theme.colors.application.typography.default,
                  fontSize: theme.font.fontSize.small,
                }}
              >
                {t(
                  'accounting:incomingInvoices.drawerSteps.controlling.customCompany'
                )}
              </span>
            </Flex.Row>
          </Flex.Column>

          <Form.Item
            name="toOfficeId"
            label={t('accounting:incomingInvoices.fields.toOfficeId.label')}
            rules={[
              requiredValueRule(
                t('accounting:incomingInvoices.fields.toOfficeId.requiredText')
              ),
            ]}
            className={classes.formItem}
          >
            <OfficePicker
              multiple={false}
              value={toOfficeId}
              onChange={(val) => {
                const officeId = Array.isArray(val) ? (val[0] ?? null) : val;
                form.setFieldsValue({
                  toOfficeId: officeId,
                  sharedWithOfficeIds: officeId ? [officeId] : [],
                });
              }}
            />
          </Form.Item>
        </div>
      </div>
    </Flex.Column>
  );
};

export default IncomingInvoicesDrawerControllingStep;
